import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const SIZES = [
  "Size Newborn", "Size 1", "Size 2",
  "Size 3", "Size 4", "Size 5", "Size 6",
  "Size 7", "Size 8"
];

const SHEETS_PER_SIZE = 35;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const STORAGE_KEY = 'priceHuntData';
const ITEMS_POLL_MS = 4000; // concurrent users see Saves without manual refresh

async function fetchItemsBySize(size) {
  const res = await fetch(`${API_URL}/api/items?size=${encodeURIComponent(size)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Failed to load items (${res.status})`);
  }
  const result = await res.json();
  return Array.isArray(result.items) ? result.items : [];
}

const createEmptySheet = () => ({ itemName: "", rawText: "" });

const createEmptyData = () => {
  const data = {};
  SIZES.forEach(size => {
    data[size] = Array.from({ length: SHEETS_PER_SIZE }, createEmptySheet);
  });
  return data;
};

const loadFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const full = createEmptyData();
      SIZES.forEach(size => {
        if (parsed[size] && Array.isArray(parsed[size])) {
          parsed[size].forEach((sheet, i) => {
            if (i < SHEETS_PER_SIZE) {
              full[size][i] = {
                itemName: sheet.itemName || "",
                rawText: sheet.rawText || ""
              };
            }
          });
        }
      });
      return full;
    }
  } catch (e) {
    console.error('Failed to load data', e);
  }
  return createEmptyData();
};


function parseRow(text) {
    let parts = text.trim().split(/\t+/).map(p => p.trim()).filter(p => p !== "");
    if (parts.length < 3) return null;
  
    return {
      store: parts[0] || "",                    // Store
      currentPrice: parts[1] || "",             // Current Price
      currentEarn: parts[2] || "",              // Earn Points / Coupon (current)
      currentRemarks: parts[3] || "",           // Remarks (current)
      lowestPrice: parts[4] || "",              // 4-week Lowest $
      lowestEarn: parts[5] || "",               // Earn Points / Coupon (4-week lowest)
      lowestRemarks: parts[6] || ""             // Remarks (4-week lowest)
    };
  }


function App() {
  const [data, setData] = useState(loadFromStorage);
  const [activeSize, setActiveSize] = useState("Size Newborn");
  const [mainViewSize, setMainViewSize] = useState("Size Newborn");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [dbItems, setDbItems] = useState([]);
  const [mainViewLoading, setMainViewLoading] = useState(false);
  const [mainViewError, setMainViewError] = useState('');
  const [saveState, setSaveState] = useState({}); // { [size|index]: { status, message } }
  const [isNarrow, setIsNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches
  );

  // Draft auto-save for Input Area fields
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px)');
    const onChange = (e) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const fetchItemsForSize = async (size) => {
    setMainViewLoading(true);
    setMainViewError('');
    try {
      const res = await fetch(`${API_URL}/api/items?size=${encodeURIComponent(size)}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to load items (${res.status})`);
      }
      const result = await res.json();
      setDbItems(Array.isArray(result.items) ? result.items : []);
    } catch (err) {
      console.error(err);
      setDbItems([]);
      setMainViewError(err.message || 'Cannot load saved deals from server.');
    } finally {
      setMainViewLoading(false);
    }
  };

  useEffect(() => {
    fetchItemsForSize(mainViewSize);
  }, [mainViewSize]);

  const updateItemName = (size, index, value) => {
    setData(prev => {
      const copy = { ...prev };
      copy[size] = [...copy[size]];
      copy[size][index] = { ...copy[size][index], itemName: value };
      return copy;
    });
  };

  const updateRawText = (size, index, value) => {
    setData(prev => {
      const copy = { ...prev };
      copy[size] = [...copy[size]];
      copy[size][index] = { ...copy[size][index], rawText: value };
      return copy;
    });
  };

  const handleLogin = async () => {
    setIsVerifying(true);
    setPasswordError('');
    try {
      const res = await fetch(`${API_URL}/api/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });
      const result = await res.json();
      if (result.success) {
        setIsAuthenticated(true);
      } else {
        setPasswordError('Incorrect password');
      }
    } catch (err) {
      setPasswordError('Cannot connect to server. Make sure backend is running.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSaveSheet = async (size, index) => {
    const sheet = (data[size] || [])[index];
    const saveKey = `${size}|${index}`;
    if (!sheet) return;

    const itemName = (sheet.itemName || '').trim();
    if (!itemName) {
      setSaveState(prev => ({
        ...prev,
        [saveKey]: { status: 'error', message: 'Enter Item Name (Field 1) before saving.' }
      }));
      return;
    }

    const rowTexts = (sheet.rawText || '').split(';').map(r => r.trim()).filter(Boolean);
    const rows = [];
    rowTexts.forEach(rowText => {
      const parsed = parseRow(rowText);
      if (!parsed || !parsed.store) return;
      rows.push({
        store: parsed.store,
        current_price: parsed.currentPrice,
        current_after_pts: parsed.currentEarn,
        current_rmk: parsed.currentRemarks,
        '4-week_lowest_price': parsed.lowestPrice,
        '4-week_lowest_after_pts': parsed.lowestEarn,
        '4-week_lowest_rmk': parsed.lowestRemarks
      });
    });

    if (rows.length === 0) {
      setSaveState(prev => ({
        ...prev,
        [saveKey]: { status: 'error', message: 'Paste at least one valid Field 2 row before saving.' }
      }));
      return;
    }

    setSaveState(prev => ({
      ...prev,
      [saveKey]: { status: 'saving', message: 'Saving…' }
    }));

    try {
      const res = await fetch(`${API_URL}/api/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: passwordInput,
          size_tab: size,
          item_name: itemName,
          rows
        })
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result.error || result.message || `Save failed (${res.status})`);
      }
      setSaveState(prev => ({
        ...prev,
        [saveKey]: {
          status: 'ok',
          message: `Saved ${result.count || rows.length} row(s) for “${itemName}”.`
        }
      }));
      if (size === mainViewSize) {
        fetchItemsForSize(mainViewSize);
      }
    } catch (err) {
      setSaveState(prev => ({
        ...prev,
        [saveKey]: { status: 'error', message: err.message || 'Save failed.' }
      }));
    }
  };

  // Main View data from SQLite (grouped + sorted)
  const groupedMainView = useMemo(() => {
    const groups = {};

    dbItems.forEach((item) => {
      const itemName = (item.item_name || '').trim() || 'Untitled';
      const currentEarn = parseFloat(item.current_after_pts) || 9999;

      if (!groups[itemName]) groups[itemName] = { itemName, rows: [] };

      groups[itemName].rows.push({
        store: item.store || '',
        currentPrice: item.current_price || '',
        currentEarn: item.current_after_pts || '',
        currentRemarks: item.current_rmk || '',
        lowestPrice: item['4-week_lowest_price'] || '',
        lowestEarn: item['4-week_lowest_after_pts'] || '',
        lowestRemarks: item['4-week_lowest_rmk'] || '',
        status: item.deal_status || 'Low',
        sortKey: currentEarn
      });
    });

    const result = Object.values(groups).sort((a, b) =>
      a.itemName.localeCompare(b.itemName)
    );

    result.forEach(g => {
      g.rows.sort((a, b) => a.sortKey - b.sortKey);
      g.rows.forEach((row, idx) => {
        row.priority = idx + 1;
      });
    });

    return result;
  }, [dbItems]);

  const colAlign = isNarrow ? 'left' : undefined;
  const thBase = {
    ...styles.th,
    ...(isNarrow ? styles.thNarrow : {})
  };
  const cellBase = isNarrow ? styles.cellNarrow : {};
  const emphasize = {
    ...styles.emphasizeCol,
    ...(isNarrow ? styles.emphasizeColNarrow : {})
  };
  const colWidths = isNarrow
    ? ['5%', '14%', '11%', '11%', '12%', '11%', '11%', '12%', '13%']
    : ['4%', '14%', '11%', '12%', '12%', '11%', '12%', '12%', '12%'];

  return (
    <div style={{ ...styles.container, ...(isNarrow ? styles.containerNarrow : {}) }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        html, body, #root { max-width: 100%; overflow-x: hidden; }
      `}</style>
      <h1 style={styles.title}>Same For Less · Save For More</h1>
      <p style={styles.subtitle}>
        Diapers · 5 Nov 2026 - 11 Nov 2026 · Updated Weekly
      </p>

      {/* ========== MAIN VIEW SIZE TABS ========== */}
      <div style={styles.sectionLabel}>Select Size</div>
      <div style={styles.tabs}>
        {SIZES.map(size => (
          <div
            key={size}
            style={{
              ...styles.tab,
              ...(isNarrow ? styles.tabNarrow : {}),
              ...(mainViewSize === size ? styles.tabActive : {})
            }}
            onClick={() => setMainViewSize(size)}
          >
            {size}
          </div>
        ))}
      </div>

      {/* ========== MAIN VIEW ========== */}
      <div style={{ ...styles.mainView, ...(isNarrow ? styles.mainViewNarrow : {}) }}>
        <h2 style={styles.mainViewTitle}>
        5 Nov 2026 - 11 Nov 2026 — {mainViewSize}
        </h2>

        {mainViewLoading ? (
          <div style={styles.emptyMsg}>Loading saved deals…</div>
        ) : mainViewError ? (
          <div style={{ ...styles.emptyMsg, color: '#c62828', fontStyle: 'normal' }}>
            {mainViewError}
          </div>
        ) : groupedMainView.length === 0 ? (
          <div style={styles.emptyMsg}>No saved data yet for this size. Unlock Input Area and click Save.</div>
        ) : (
          groupedMainView.map((group, idx) => (
            <div key={idx} style={styles.itemSection}>
              <div style={{ ...styles.itemHeader, ...(isNarrow ? styles.itemHeaderNarrow : {}) }}>
                {group.itemName}
              </div>
              <div style={styles.tableWrap}>
                <table style={{ ...styles.table, ...(isNarrow ? styles.tableNarrow : {}) }}>
                  <colgroup>
                    {colWidths.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead>
                  <tr>
                      <th style={{ ...thBase, textAlign: 'center' }}>#</th>
                      <th style={{ ...thBase, ...styles.store_header, textAlign: 'left' }}>Store</th>
                      <th style={{ ...thBase, ...styles.currentCol, textAlign: colAlign || 'center' }}>
                        Current Price
                      </th>
                      <th style={{ ...thBase, ...styles.currentCol, textAlign: colAlign || 'center' }}>
                        Earn Pts / C.p
                      </th>
                      <th style={{ ...thBase, ...styles.currentCol, textAlign: colAlign || 'center' }}>
                        Rmk
                      </th>
                      <th style={{ ...thBase, textAlign: colAlign || 'center' }}>
                        4-week Lowest $
                      </th>
                      <th style={{ ...thBase, textAlign: colAlign || 'center' }}>
                        Earn Pts / C.p
                      </th>
                      <th style={{ ...thBase, textAlign: colAlign || 'center' }}>
                        Rmk
                      </th>
                      <th style={{ ...thBase, textAlign: colAlign || 'center' }}>
                        Deal Status
                      </th>
                  </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((r, i) => (
                      <tr key={i} style={{ background: '#fff' }}>
                        <td style={{ ...styles.priority, ...cellBase }}>{r.priority}</td>
                        <td style={{ ...styles.store, ...cellBase, ...emphasize }}>{r.store}</td>
                        <td style={{ ...styles.curr_price, ...cellBase, ...emphasize }}>
                          {r.currentPrice ? `$\t${r.currentPrice}` : "—"}
                        </td>
                        <td style={{ ...styles.curr_price, ...cellBase, ...emphasize }}>
                          {r.currentEarn === r.currentPrice ? "—" : `$\t${r.currentEarn}`}
                        </td>
                        <td style={{ ...styles.curr_price, ...cellBase, ...emphasize }}>
                          {r.currentRemarks || "—"}
                        </td>
                        <td style={{ ...styles.price, ...cellBase }}>{r.lowestPrice ? `$\t${r.lowestPrice}` : "—"}</td>
                        <td style={{ ...styles.price, ...cellBase }}>{r.lowestEarn === r.lowestPrice ? "—" : `$\t${r.lowestEarn}`}</td>
                        <td style={{ ...styles.remarks, ...cellBase }}>
                          {r.lowestRemarks || "—"}
                        </td>
                        <td style={{
                          ...cellBase,
                          color:  r.status === "Best Deal !" ? "#8A380F" :
                                  r.status === "High"      ? "#2e7d32" :
                                  r.status === "Average"   ? "#7B619E" : "#AD6F52",
                          fontWeight: r.status === "Best Deal !" ? "bold" : 550,
                          fontFamily: r.status === "Best Deal !" ? "sans-serif" : "inherit",
                          textAlign: colAlign || 'center',
                          overflowWrap: 'anywhere',
                          wordBreak: 'break-word'
                        }}>
                          {isNarrow && r.status === "Best Deal !" ? "Best!" : r.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ========== PASSWORD GATE / INPUT AREA ========== */}
      {!isAuthenticated ? (
        <div style={styles.loginBox}>
          <h3 style={{ marginTop: 0, color: '#6b3a42' }}>Input Area Locked</h3>
          <p style={{ color: '#8a6a60', marginBottom: 16 }}>
            Enter password to edit data sheets
          </p>
          <input
            type="password"
            placeholder="Enter password"
            value={passwordInput}
            onChange={e => setPasswordInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={styles.passwordInput}
          />
          {passwordError && (
            <div style={{ color: '#c62828', marginTop: 8, fontSize: '0.9rem' }}>
              {passwordError}
            </div>
          )}
          <button
            onClick={handleLogin}
            disabled={isVerifying}
            style={styles.loginButton}
          >
            {isVerifying ? 'Checking...' : 'Unlock Input Area'}
          </button>
        </div>
      ) : (
        <>
          <div style={styles.sectionLabel}>
            Input Area (Unlocked)
            <button
              onClick={() => setIsAuthenticated(false)}
              style={styles.lockButton}
            >
              Lock Input Area
            </button>
          </div>

          <div style={styles.tabs}>
            {SIZES.map(size => (
              <div
                key={size}
                style={{
                  ...styles.tab,
                  ...(isNarrow ? styles.tabNarrow : {}),
                  ...(activeSize === size ? styles.tabActive : {})
                }}
                onClick={() => setActiveSize(size)}
              >
                {size}
              </div>
            ))}
          </div>

          {/* 35 sheets in original order */}
          {(data[activeSize] || []).map((sheet, index) => {
            const saveKey = `${activeSize}|${index}`;
            const sheetSave = saveState[saveKey];
            return (
            <div key={index} style={styles.itemBlock}>
              <div style={styles.itemBlockHeader}>
                <span style={styles.itemTitle}>
                  Table {index + 1} — {activeSize}
                </span>
                <button
                  type="button"
                  onClick={() => handleSaveSheet(activeSize, index)}
                  disabled={sheetSave?.status === 'saving'}
                  style={styles.saveButton}
                >
                  {sheetSave?.status === 'saving' ? 'Saving…' : 'Save'}
                </button>
              </div>

              <div style={styles.fieldLabel}>Field 1 — Item Name</div>
              <input
                type="text"
                placeholder="Input Item Name (e.g. Huggies Little Movers 88ct)"
                value={sheet.itemName}
                onChange={e => updateItemName(activeSize, index, e.target.value)}
                style={styles.input}
              />

              <div style={{ ...styles.fieldLabel, marginTop: 12 }}>
                Field 2 — Store | Current Price | Earn Pts / C.p | Rmk | 4-week Lowest $ | Earn Pts / C.p | Rmk
              </div>
              <textarea
                placeholder={`Paste here (tab separated, use ; for next row):\nRealC'Superstore\t44.94\t41.94\t3000 pts\t44.94\t41.94\t3000 pts;`}
                value={sheet.rawText}
                onChange={e => updateRawText(activeSize, index, e.target.value)}
                style={styles.textarea}
              />
              <div style={styles.hint}>
                Format: Store [Tab] Current Price [Tab] Earn Pts / C.p [Tab] Rmk [Tab] 4-week Lowest $ [Tab] Earn Pts / C.p [Tab] Rmk | Use <b>;</b> for next row.
                Click <b>Save</b> to write this sheet to SQLite with size_tab = “{activeSize}”.
              </div>
              {sheetSave?.message && (
                <div style={{
                  marginTop: 8,
                  fontSize: '0.8rem',
                  color: sheetSave.status === 'error' ? '#c62828' : '#2e7d32'
                }}>
                  {sheetSave.message}
                </div>
              )}
            </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#F8F1F2',
      color: '#333',
      margin: 0,
      padding: 20,
      minHeight: '100vh',
      maxWidth: '100%',
      overflowX: 'hidden'
    },
    containerNarrow: {
      padding: 10
    },
    title: { 
        fontFamily: 'Didot, "Bodoni MT", "Century Schoolbook", "Hoefler Text", Garamond, serif',
        fontWeight: 'bold',
        letterSpacing: '-0.03em',
        margin: '0 0 6px 0', 
        fontSize: '1.8rem', 
        color: '#6b3a42' 
    },
    subtitle: { color: '#8a6a60', marginBottom: 20, fontSize: '0.9rem' },
    sectionLabel: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: '#6b3a42',
      marginBottom: 8,
      marginTop: 12,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flexWrap: 'wrap'
    },
    tabs: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      marginBottom: 20,
      borderBottom: '1px solid #e0c8c0',
      paddingBottom: 12
    },
    tab: {
      padding: '8px 16px',
      background: '#D98787',
      border: '1px solid #c46f6f',
      borderRadius: 8,
      cursor: 'pointer',
      color: '#fff',
      fontWeight: 500,
      fontSize: '0.9rem'
    },
    tabNarrow: {
      padding: '6px 10px',
      fontSize: '0.78rem'
    },
    tabActive: {
      background: '#CE828D',
      borderColor: '#b86b75',
      fontWeight: 600
    },
    mainView: {
        background: '#D5C3BE',
        borderRadius: 12,
        padding: 16,
        marginBottom: 32,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid #c9b5b0',
        maxWidth: '100%',
        overflow: 'hidden'
    },
    mainViewNarrow: {
        padding: 8
    },
    mainViewTitle: { 
        margin: '0 0 16px 0',
        fontSize: '1.15rem',
        color: '#5a3a4a',
        overflowWrap: 'anywhere'
     },
    itemSection: {
        marginBottom: 28,
        maxWidth: '100%',
        overflow: 'hidden'
    },
    itemHeader: {
        background: '#CFA191',
        padding: '8px 14px',
        borderRadius: '8px 8px 0 0',
        fontWeight: 600,
        color: '#5a3a4a',
        fontSize: '0.95rem',
        border: '1px solid #b87aab',
        borderBottom: 'none',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word'
    },
    itemHeaderNarrow: {
        padding: '6px 8px',
        fontSize: '0.82rem'
    },
    tableWrap: {
        width: '100%',
        maxWidth: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        border: '1px solid #b87aab',
        borderTop: 'none',
        borderRadius: '0 0 8px 8px'
    },
    table: { 
        width: '100%', 
        borderCollapse: 'collapse', 
        fontSize: '0.88rem', 
        background: '#fffef8',
        tableLayout: 'fixed'
    },
    tableNarrow: {
        fontSize: '0.48rem'
    },
    th: {
        padding: '9px 11px',
        textAlign: 'left',
        borderBottom: '1px solid #e0d0cc',
        background: "#EEEEEE",
        color: '#4a3028',
        fontWeight: 600,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: 1.25,
        verticalAlign: 'top'
    },
    thNarrow: {
        padding: '4px 2px',
        fontSize: '0.48rem',
        lineHeight: 1.2,
        textAlign: 'left',
        verticalAlign: 'top'
    },
    cellNarrow: {
        padding: '4px 2px',
        fontSize: '0.48rem',
        textAlign: 'left',
        verticalAlign: 'top',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        lineHeight: 1.2
    },
    emphasizeCol: {
        fontWeight: 700
    },
    emphasizeColNarrow: {
        fontWeight: 700,
        fontSize: '0.48rem'
    },

    // Highlighted columns (Current Price / Net / Remarks)
    store_header: {
        color: '#4a3028',
        background: '#BFC8DF',
        fontWeight: 'bold',
    },
    currentCol: {
        color: '#4a3028',
        background: '#CAA0B5',
        fontWeight: 'bold',
    },
    curr_price: {
        color: '#4a3028',
        background: '#CAA0B5',
        fontWeight: 'bold',
        textAlign: 'center'
    },
    priority: { 
        fontWeight: 700, 
        color: '#5c4033', 
        whiteSpace: 'normal',
        textAlign: 'center',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: '8px 4px'
    },
    store: {
        whiteSpace: 'normal',
        textAlign: 'left',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: '8px 6px',
        background: '#BFC8DF',
    }, 
    price: { 
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'normal',
        textAlign: 'center',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: '8px 4px'
    },
    remarks: {
        color: '#7a6a55', 
        fontSize: '0.82rem',
        whiteSpace: 'normal',
        textAlign: 'center',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: '8px 4px'
    },
    emptyMsg: { color: '#9e8b75', fontStyle: 'italic', padding: '12px 0' },
    itemBlock: {
      background: '#C4B08D',
      borderRadius: 10,
      padding: '14px 16px',
      marginBottom: 18,
      border: '1px solid #b09a78',
      overflow: 'hidden'
    },
    itemBlockHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 10,
      gap: 12
    },
    itemTitle: { 
        fontWeight: 600, 
        color: '#4a3a20', 
        whiteSpace: 'nowrap' 
    },
    fieldLabel: {
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#4a3a20',
      marginBottom: 6,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word'
    },
    input: {
      background: '#fffef8',
      border: '1px solid #d4c4b0',
      color: '#333',
      padding: '6px 10px',
      borderRadius: 5,
      fontSize: '0.85rem',
      width: '100%',
      boxSizing: 'border-box',
      minWidth: 0
    },
    textarea: {
      background: '#fffef8',
      border: '1px solid #d4c4b0',
      color: '#333',
      padding: 10,
      borderRadius: 6,
      fontSize: '0.85rem',
      width: '100%',
      minHeight: 90,
      fontFamily: 'monospace',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    hint: { fontSize: '0.78rem', color: '#7a6a55', marginTop: 6 },
    loginBox: {
      background: '#D98787',
      border: '1px solid #c46f6f',
      borderRadius: 12,
      padding: 24,
      maxWidth: 400,
      margin: '20px auto',
      textAlign: 'center',
      color: '#fff',
    overflow: 'hidden'
    },
    passwordInput: {
      width: '100%',
      padding: '10px 14px',
      borderRadius: 6,
      border: '1px solid #d4c4b0',
      fontSize: '1rem',
      marginBottom: 12,
      boxSizing: 'border-box'
    },
    loginButton: {
      background: '#CE828D',
      color: 'white',
      border: 'none',
      padding: '10px 24px',
      borderRadius: 6,
      fontSize: '1rem',
      cursor: 'pointer',
      fontWeight: 600
    },
    lockButton: {
      background: '#8a4a52',
      color: 'white',
      border: 'none',
      padding: '5px 12px',
      borderRadius: 5,
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontWeight: 500
    },
    saveButton: {
      background: '#5a7a4a',
      color: 'white',
      border: 'none',
      padding: '6px 14px',
      borderRadius: 5,
      fontSize: '0.85rem',
      cursor: 'pointer',
      fontWeight: 600,
      whiteSpace: 'nowrap'
    }
  };

export default App;